import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { ItemDto, ItemStockDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api, ApiFailure } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { Barcode, dec, pct, qty } from "./common";
import { amt, when } from "../ledger/common";

// Items › item detail, ported from the prototype (erp-items.js "/items/show/:id"): item data, units with
// barcodes and prices, the item kind and tax type, the first barcode drawn, and from the stock ledger (phase 8) the
// stock by warehouse and the latest movements, in base units, within the user's warehouse scope.

function Card({ title, count, children, raw, actions }: { title: string; count?: number; children: ReactNode; raw?: boolean; actions?: ReactNode }) {
  return <section className="card"><div className="card-head"><h2>{title}{count != null && <> <span className="count num">{count}</span></>}</h2>{actions}</div>{raw ? children : <div className="card-body">{children}</div>}</section>;
}

export function ItemShow() {
  const { id } = useParams();
  const { t, L } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const [it, setIt] = useState<ItemDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [stock, setStock] = useState<ItemStockDto | null>(null);
  useEffect(() => {
    setIt(null); setFailed(null); setStock(null);
    api<ItemDto>("GET", `/items/${id}`).then(setIt, (e) => setFailed(e instanceof ApiFailure && e.status === 404 ? t("itemNotFound") : errorText(t, e)));
    api<ItemStockDto>("GET", `/stock/items/${id}`).then(setStock, () => setStock({ rows: [], moves: [] }));
  }, [id, t]);

  const crumbs = (x?: string): [string, string?][] => [[t("home"), "/home"], [t("items")], [t("allItems"), "/items/allItems"], ...(x ? [[x] as [string]] : [])];
  if (!it) return (
    <Shell navKey="/items/allItems"><PageHead crumbs={crumbs()} title={t("allItems")} />
      <section className="card"><div className={`state-box ${failed ? "error" : ""}`}>{failed ? <><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p></> : <p>{t("loading")}</p>}</div></section>
    </Shell>
  );

  const dash = <span className="muted">–</span>;
  const kv: [string, ReactNode][] = [
    [t("nameAr"), <span lang="ar">{it.nameAr}</span>], [t("nameEn"), <span lang="en">{it.nameEn}</span>],
    [t("categoryTitleAr"), it.categoryNameAr], [t("categoryTitleEn"), it.categoryNameEn],
    [t("itemCode"), it.code ? <span className="num" dir="ltr">{it.code}</span> : dash], [t("minQty"), <span className="num">{qty(it.minQty)}</span>],
    [t("itemKind"), it.itemKind ? <>{L(it.itemKind)}{!it.itemKind.hasQty && <> <span className="pill">{t("hasNoStock")}</span></>}</> : dash],
    [t("taxType"), it.taxType ? <>{L(it.taxType)} <span className="num muted">{pct(it.taxType.percentage)}</span></> : dash],
    [t("discount"), <span className="num">{pct(it.discount)}</span>], [t("priceIncludeTax"), t(it.priceIncludeTax ? "yes" : "no")], [t("tobItemFlag"), t(it.tobaccoFee ? "yes" : "no")],
    [t("quickList"), t(it.quickList ? "yes" : "no")], [t("descriptionAr"), it.descriptionAr || dash], [t("descriptionEn"), it.descriptionEn || dash],
    ...(it.legacyFlags.length ? [[t("journalLegacyFlags"), <span data-testid="item-legacy-flags">{it.legacyFlags.map((f) => <span key={f} className="pill">{t(`itemFlag_${f}`)}</span>)}</span>] as [string, ReactNode]] : [])
  ];
  const first = it.units[0]?.barcodes[0] ?? it.code;
  const actions = s.can("itemUpdate") ? <button className="btn btn-primary" onClick={() => nav(`/items/edit/${it.id}`)}><Icon name="edit" />{t("editItem")}</button> : null;

  return (
    <Shell navKey="/items/allItems">
      <PageHead crumbs={crumbs(L(it))} title={L(it)} actions={actions} />
      <div className="detail-grid"><div className="detail-main">
        <Card title={t("itemData")}>
          <div className="item-head">
            {it.image ? <img className="item-img" src={`/api/items/${it.id}/image?v=${encodeURIComponent(it.image.updatedAt)}`} alt="" data-testid="item-image" /> : <span className="item-img ph"><Icon name="box" /></span>}
            <div className="grow"><dl className="kv" style={{ ["--kv-cols" as string]: 4 }}>{kv.map(([k, x]) => <div key={k}><dt>{k}</dt><dd>{x}</dd></div>)}</dl></div>
          </div>
          {it.legacyFlags.length > 0 && <div className="dep-note" data-testid="item-legacy-note"><Icon name="alert" /><span>{t("itemLegacyNote")}</span></div>}
        </Card>
        <Card title={t("units")} count={it.units.length} raw>
          <div className="table-wrap"><table className="tbl" data-testid="item-units">
            <thead><tr><th>{t("unit")}</th><th>{t("unitBarcode")}</th><th className="end">{t("piecesNo")}</th><th className="end">{t("price_wholesale")}</th><th className="end">{t("price_half")}</th><th className="end">{t("price_retail")}</th></tr></thead>
            <tbody>{it.units.map((u) => (
              <tr key={u.id}>
                <td><b>{L({ nameEn: u.unitNameEn, nameAr: u.unitNameAr })}</b></td>
                <td>{u.barcodes.length ? u.barcodes.map((b) => <span key={b} className="num bc-chip" dir="ltr" style={{ marginInlineEnd: 4 }}>{b}</span>) : dash}</td>
                <td className="end num">{qty(u.pieces)}</td>
                <td className="end num">{dec(u.priceWholesale)}</td><td className="end num">{dec(u.priceHalf)}</td><td className="end num">{dec(u.priceRetail)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        </Card>
        <Card title={t("recentMovements")} count={stock?.moves.length} raw
          actions={s.can("r8warehouseItems") ? <Link className="btn btn-sm" to={`/reports/item-movement?item=${it.id}`} data-testid="item-movement-link"><Icon name="chart" />{t("itemMovement")}</Link> : undefined}>
          {stock?.moves.length ? <div className="table-wrap"><table className="tbl" data-testid="item-moves">
            <thead><tr><th>{t("date")}</th><th>{t("invoiceNo")}</th><th>{t("warehouse")}</th><th className="end">{t("qty")}</th><th className="end">{t("total")}</th></tr></thead>
            <tbody>{stock.moves.map((m) => (
              <tr key={m.seq}>
                <td className="num">{when(m.movedAt)}</td>
                <td><Link className="num" to={`/invoices/show/${m.document.id}`}>{m.document.code}</Link>{m.reversal && <> <span className="pill">{t("journalReversed")}</span></>}</td>
                <td>{L(m.warehouse)}</td>
                <td className={`end num ${m.qty.startsWith("-") ? "qty-out" : "qty-in"}`}>{m.qty.startsWith("-") ? "−" + qty(m.qty.slice(1)) : "+" + qty(m.qty)}</td>
                <td className="end num">{amt(m.value)}</td>
              </tr>
            ))}</tbody>
          </table></div> : <div className="card-body"><div className="empty sm"><Icon name="box" /><div>{stock ? t("noStockRows") : t("loading")}</div></div></div>}
        </Card>
      </div>
      <aside className="detail-side">
        <Card title={t("stockByWarehouse")} raw>
          {it.itemKind && !it.itemKind.hasQty ? <div className="card-body"><span className="pill">{t("hasNoStock")}</span></div>
            : stock?.rows.length ? <div className="table-wrap"><table className="tbl" data-testid="item-stock">
              <thead><tr><th>{t("warehouse")}</th><th className="end">{t("qty")}</th><th className="end">{t("averagePrice")}</th></tr></thead>
              <tbody>{stock.rows.map((r) => <tr key={r.warehouse.id}><td>{L(r.warehouse)}</td><td className="end num">{qty(r.qty)} <small className="muted">{L(r.baseUnit)}</small></td><td className="end num">{dec(r.averageCost)}</td></tr>)}</tbody>
            </table></div> : <div className="card-body"><div className="empty sm"><Icon name="warehouse" /><div>{stock ? t("noStockRows") : t("loading")}</div></div></div>}
        </Card>
        <Card title={t("barcode")}><div className="bc-preview"><Barcode text={first} /><div className="num" dir="ltr">{first}</div></div></Card>
      </aside></div>
    </Shell>
  );
}
